// ----------------------------------------------------------------------------
// AuthContext
// ----------------------------------------------------------------------------
// Top-level session gate. Holds a single `isAuthenticated` flag plus a stub
// `currentUser` profile (email/company). The dashboard tree is only rendered
// when `isAuthenticated === true`.
//
// PRODUCTION LAUNCH NOTE:
//   const supabase = createClient(ownerSettings.supabaseUrl,
//                                 ownerSettings.supabaseKey);
//   const { data, error } =
//     await supabase.auth.signInWithPassword({ email, password });
//   setAuthenticated(!!data.session);
//
// That single call replaces the visual 1.5-second simulation below with a
// real multi-tenant Supabase/Firebase session — RLS automatically scopes the
// rest of the dashboard to the signed-in vendor's rows.
// ----------------------------------------------------------------------------

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const signIn = useCallback((profile) => {
    setCurrentUser(profile);
    setIsAuthenticated(true);
  }, []);

  const signOut = useCallback(() => {
    setIsAuthenticated(false);
    setCurrentUser(null);
  }, []);

  const value = useMemo(
    () => ({ isAuthenticated, currentUser, signIn, signOut }),
    [isAuthenticated, currentUser, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
